import React from 'react';
import { createRoot } from 'react-dom/client';
import { useCallback } from 'react';
import { useAuth } from 'react-oidc-context';
import { jsPDF } from 'jspdf';
import { toPng, getFontEmbedCSS } from 'html-to-image';
import type { CourseTeeSetData, EventScoreWithId, scoreHole } from '../../../shared/config/iTournamentBuilder';
import { SCORECARD_CANVAS_W, SCORECARD_CANVAS_H } from './scorecardTemplateTypes';
import type { ScorecardTemplate, ScoringUnit } from './scorecardTemplateTypes';
import { ScorecardCardItem } from './ScorecardCardItem';

// ─── Layout types ─────────────────────────────────────────────────────────────
// 1-up: card-size paper (792 × 306 pt), one card per page, no margins.
// 2-up: letter landscape (792 × 612 pt), two cards stacked, dashed cut guide.

export type ScorecardPDFLayout = '1up' | '2up';

export interface GenerateScorecardPDFOptions {
    /** Scoring units to render — single item = "Print Selected", full list = "Print All". */
    units: ScoringUnit[];
    template: ScorecardTemplate;
    layout: ScorecardPDFLayout;
    /** tbEvent cast to a loose map (for binding resolution and filename). */
    eventData: Record<string, unknown>;
    /** Primary course tee-set data for hole par/dist/hdcp. */
    courseData: CourseTeeSetData | null;
    /** All EventScoreWithId records for this event. */
    scores: EventScoreWithId[];
    roundValue: number;
    /** When true, registration-level bindings and labels are suppressed. */
    blankMode?: boolean;
}

// ─── Dimension constants ──────────────────────────────────────────────────────
// Render at 1× the PDF point size so the image maps 1 px → 1 pt in jsPDF.

const CARD_W_PT = SCORECARD_CANVAS_W; // 792 pt  (11")
const CARD_H_PT = SCORECARD_CANVAS_H; // 306 pt  (4.25")

// ─── Hook ─────────────────────────────────────────────────────────────────────

export function useScorecardPDF() {
    const auth = useAuth();

    const generatePDF = useCallback(async (opts: GenerateScorecardPDFOptions) => {
        const { units, template, layout, eventData, courseData, scores, roundValue, blankMode = false } = opts;

        if (units.length === 0) return;

        // Resolve any CDN image URLs in the template to base64 via proxy
        const resolvedTemplate = await resolveTemplateImages(
            template,
            process.env.REACT_APP_API_URL ?? '',
            auth.user?.access_token,
        );

        // Ensure all web fonts (Fredoka, etc.) are loaded before capture
        await document.fonts.ready;

        // Resolve per-unit score + marker data
        const unitDataArr = units.map((unit) =>
            resolveUnitData(unit, scores, units, roundValue),
        );

        // Render all cards off-screen and capture as PNGs
        const pngs = await captureCardsAsPngs(
            resolvedTemplate,
            units,
            unitDataArr,
            eventData,
            courseData,
            roundValue,
            blankMode,
        );

        // Assemble and save the PDF
        const eventName   = String(eventData.eventName ?? 'Event').replace(/[^a-z0-9]/gi, '_');
        const layoutLabel = layout === '1up' ? 'singles' : 'doubles';
        const scoreLabel  = scores.length > 0 ? 'filled' : 'blank';
        const regLabel    = blankMode ? 'reg_blank' : 'reg_filled';
        const fileSuffix  = units.length === 1
            ? `${units[0].playerName.replace(/[^a-z0-9]/gi, '_')}_scorecard_${layoutLabel}_${scoreLabel}_${regLabel}`
            : `scorecards_all_${layoutLabel}_${scoreLabel}_${regLabel}`;

        if (layout === '1up') {
            assemblePDF1Up(pngs, eventName, fileSuffix);
        } else {
            assemblePDF2Up(pngs, eventName, fileSuffix);
        }
    }, [auth]);

    return { generatePDF };
}

// ─── Per-unit data resolution ─────────────────────────────────────────────────

interface UnitRenderData {
    unitScores: EventScoreWithId | null;
    markerScores: scoreHole[];
    markerLabel: string;
    markerScoreCode: string;
}

function resolveUnitData(
    unit: ScoringUnit,
    scores: EventScoreWithId[],
    allUnits: ScoringUnit[],
    roundValue: number,
): UnitRenderData {
    // Find this unit's EventScoreWithId
    const unitScores = scores.find(
        (s) => String((s as any)._id) === String(unit.eventScoreID),
    ) ?? null;

    const roundScore = unitScores?.roundScores?.find(
        (rs) => rs.roundNumber === roundValue,
    );
    const markerScoreCode = roundScore?.markerScoreCode ?? null;

    let markerScores: scoreHole[] = [];
    let markerLabel = '';
    let resolvedMarkerScoreCode = '';

    if (markerScoreCode) {
        // Find the marker's EventScoreWithId — the one whose roundScore for this
        // round has playerScoreCode matching the marker score code.
        const markerEventScore = scores.find((s) =>
            s.roundScores?.some(
                (rs) =>
                    rs.roundNumber === roundValue &&
                    rs.playerScoreCode === markerScoreCode,
            ),
        );
        if (markerEventScore) {
            markerScores =
                markerEventScore.roundScores.find(
                    (rs) => rs.roundNumber === roundValue,
                )?.holeScores ?? [];
        }

        resolvedMarkerScoreCode = markerScoreCode;

        // Collect all names for the marker score code and join them (mirrors buildScoringUnits playerName)
        const markerNames: string[] = [];
        for (const u of allUnits) {
            for (const reg of u.registrations) {
                if (reg.scoreCode === markerScoreCode) {
                    markerNames.push(reg.playerName ?? '');
                }
            }
        }
        markerLabel = markerNames.filter(Boolean).join(' / ');
    }

    return { unitScores, markerScores, markerLabel, markerScoreCode: resolvedMarkerScoreCode };
}

// ─── Off-screen render + PNG capture ─────────────────────────────────────────
// Mounts one ScorecardCardItem per unit in a hidden off-screen container,
// waits for React + QR code effects to settle, then captures each as a PNG.

async function captureCardsAsPngs(
    template: ScorecardTemplate,
    units: ScoringUnit[],
    unitDataArr: UnitRenderData[],
    eventData: Record<string, unknown>,
    courseData: CourseTeeSetData | null,
    roundValue: number,
    blankMode: boolean,
): Promise<string[]> {
    const container = document.createElement('div');
    Object.assign(container.style, {
        position:      'fixed',
        top:           '0px',
        left:          '-9999px',
        display:       'flex',
        flexDirection: 'column',
        pointerEvents: 'none',
        zIndex:        '-1',
    });
    document.body.appendChild(container);

    const wrappers: HTMLDivElement[] = units.map(() => {
        const div = document.createElement('div');
        container.appendChild(div);
        return div;
    });

    const roots = wrappers.map((div, i) => {
        const { unitScores, markerScores, markerLabel, markerScoreCode } = unitDataArr[i];
        const root = createRoot(div);
        root.render(
            React.createElement(ScorecardCardItem, {
                template,
                scoringUnit:     units[i],
                eventData,
                courseData,
                scores:          unitScores,
                markerScores:    markerScores.length > 0 ? markerScores : null,
                markerLabel,
                markerScoreCode,
                roundValue,
                blankMode,
                displayWidth:    CARD_W_PT,
                showBorder:      false,
            }),
        );
        return root;
    });

    // 600 ms gives React flush + QR code useEffects ample time to complete
    await new Promise<void>((resolve) => setTimeout(resolve, 600));

    // Pre-compute font embed CSS once so html-to-image doesn't re-fetch
    // Google Fonts for each individual card capture.
    const firstEl = wrappers[0]?.firstElementChild as HTMLElement | null;
    const fontEmbedCSS = firstEl ? await getFontEmbedCSS(firstEl) : '';

    // pixelRatio = 300 DPI ÷ 72 pt/in ≈ 4.167 — produces a 300 PPI image in the PDF
    const PRINT_PIXEL_RATIO = 300 / 72;
    const pngs = await Promise.all(
        wrappers.map((div) =>
            toPng(div.firstElementChild as HTMLElement, { pixelRatio: PRINT_PIXEL_RATIO, fontEmbedCSS }),
        ),
    );

    roots.forEach((root) => root.unmount());
    document.body.removeChild(container);

    return pngs;
}

// ─── PDF assembly — 1-up ─────────────────────────────────────────────────────
// Card-size paper (792 × 306 pt), one card per page, placed at (0, 0).

function assemblePDF1Up(pngs: string[], eventName: string, fileSuffix: string) {
    const doc = new jsPDF({ unit: 'pt', format: [CARD_W_PT, CARD_H_PT], orientation: 'landscape' });

    pngs.forEach((png, i) => {
        if (i > 0) doc.addPage();
        doc.addImage(png, 'PNG', 0, 0, CARD_W_PT, CARD_H_PT);
    });

    doc.save(`${eventName}_${fileSuffix}.pdf`);
}

// ─── PDF assembly — 2-up ─────────────────────────────────────────────────────
// Letter landscape (792 × 612 pt), two cards stacked. Dashed cut guide drawn
// at y = 306 on pages that have both slots filled.

function assemblePDF2Up(pngs: string[], eventName: string, fileSuffix: string) {
    const PAGE_H = CARD_H_PT * 2; // 612 pt
    const doc = new jsPDF({ unit: 'pt', format: [CARD_W_PT, PAGE_H], orientation: 'landscape' });

    pngs.forEach((png, i) => {
        const slotOnPage = i % 2;

        // New page for every first-slot card after the first
        if (slotOnPage === 0 && i > 0) doc.addPage();

        const y = slotOnPage * CARD_H_PT;
        doc.addImage(png, 'PNG', 0, y, CARD_W_PT, CARD_H_PT);

        // After placing the second card on a page, overlay a dashed cut guide
        // at the midpoint so the printer knows where to cut.
        if (slotOnPage === 1) {
            doc.setDrawColor(150, 150, 150);
            doc.setLineDashPattern([5, 4], 0);
            doc.setLineWidth(0.5);
            doc.line(0, CARD_H_PT, CARD_W_PT, CARD_H_PT);
            doc.setLineDashPattern([], 0); // reset to solid
        }
    });

    doc.save(`${eventName}_${fileSuffix}.pdf`);
}

// ─── Template image resolver ──────────────────────────────────────────────────
// Converts any CDN image URLs in the template to base64 data URLs via the
// server proxy so html-to-image can embed them without CORS issues.

async function resolveTemplateImages(
    template: ScorecardTemplate,
    apiBase: string,
    accessToken: string | undefined,
): Promise<ScorecardTemplate> {
    const resolvedElements = await Promise.all(
        template.elements.map(async (el) => {
            if (el.type !== 'image' || !el.imageDataUrl) return el;
            if (el.imageDataUrl.startsWith('data:')) return el;

            try {
                const fetchUrl = `${apiBase}/image/proxyImage?url=${encodeURIComponent(el.imageDataUrl)}`;
                const headers: HeadersInit = accessToken
                    ? { Authorization: `Bearer ${accessToken}` }
                    : {};
                const res = await fetch(fetchUrl, { headers });
                if (!res.ok) throw new Error(`Proxy ${res.status}`);
                const blob = await res.blob();
                return { ...el, imageDataUrl: await blobToDataUrl(blob) };
            } catch (err) {
                console.warn(
                    'Scorecard image fetch failed, keeping original URL:',
                    el.imageDataUrl,
                    err,
                );
                return el;
            }
        }),
    );
    return { ...template, elements: resolvedElements };
}

function blobToDataUrl(blob: Blob): Promise<string> {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload  = () => resolve(reader.result as string);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
    });
}
